import React, {useContext, useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {doc, onSnapshot, updateDoc} from "firebase/firestore";
import {db} from "../../Firebase";
import {kLibrettoUserCollectionName} from "../../utils/CollectionConstants";
import {LibrettoPlan, formatUnixTimeBrief, normalizeTimestampToMillis} from "../../utils/utils";
import {FetchContext} from "../../context/FetchContext";
import {loadStripe} from "@stripe/stripe-js";

const TAB_PROFILE = "profile";
const TAB_PLAN = "plan";
const TAB_USAGE = "usage";
const StripePromise = loadStripe('pk_live_51M1XiRBj7f2nVTUZCtLaBjIfMqKiA8OhnoQxOSddZwAJMktMfnlYz1pIrYVdxu9Yoato4ZIRjKZyv7oRp41OpDDJ00dgvuYP97');

const sidebarHeaderStyle = {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    marginTop: "10px",
};

const sidebarItemStyle = (active) => ({
    display: "flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: active ? "var(--app-bg-subtle)" : "transparent",
    color: active ? "var(--app-text-primary)" : "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    transition: "background 0.12s ease",
    "&:hover": {
        backgroundColor: "var(--app-bg-subtle)",
    },
});

const sectionTitleStyle = {
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 600,
    color: "var(--app-text-primary)",
};

const labelTextStyle = {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 500,
    color: "var(--app-text-muted)",
};

const valueTextStyle = {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    color: "var(--app-text-primary)",
};

const mutedTextStyle = {
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    lineHeight: "20px",
};

const planCardStyle = {
    border: "1px solid var(--app-border-subtle)",
    borderRadius: "12px",
    padding: "16px",
    backgroundColor: "var(--app-bg-surface)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};

const planPrimaryButtonStyle = {
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: "Inter",
    fontWeight: 600,
    backgroundColor: "#2B6BFD",
    height: "35px",
    minHeight: "35px",
    padding: "0 16px",
    "&:hover": {
        backgroundColor: "#245DDB",
    },
    "&.Mui-disabled": {
        backgroundColor: "var(--app-border-muted)",
        color: "var(--app-text-subtle)",
    },
};

const planSecondaryButtonStyle = {
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: "Inter",
    fontWeight: 600,
    color: "var(--app-text-primary)",
    borderColor: "var(--app-border-muted)",
    height: "35px",
    minHeight: "35px",
    padding: "0 16px",
    "&:hover": {
        borderColor: "var(--app-border-muted)",
        backgroundColor: "var(--app-bg-subtle)",
    },
};

const planInlineRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
};

const paymentMethodIconStyle = {
    width: "36px",
    height: "24px",
    borderRadius: "6px",
    border: "1px solid var(--app-border-subtle)",
    backgroundColor: "var(--app-bg-canvas)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--app-text-muted)",
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
};

const paymentMethodImageStyle = {
    width: "36px",
    height: "24px",
    borderRadius: "6px",
    border: "1px solid var(--app-border-subtle)",
    backgroundColor: "var(--app-bg-surface)",
    objectFit: "cover",
    display: "block",
};

const removePaymentLinkStyle = {
    alignSelf: "flex-start",
    marginTop: "2px",
    padding: 0,
    minWidth: "auto",
    textTransform: "none",
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 500,
    color: "var(--app-text-muted)",
    "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
    },
};

const paymentNoticeStyle = {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    lineHeight: "18px",
};

const usageSummaryCardStyle = {
    border: "1px solid var(--app-border-subtle)",
    borderRadius: "14px",
    padding: "18px",
    backgroundColor: "var(--app-bg-surface)",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
};

const usageProgressTrackStyle = {
    width: "100%",
    height: "44px",
    borderRadius: "12px",
    overflow: "hidden",
    position: "relative",
    border: "1px solid var(--app-border-subtle)",
    backgroundColor: "var(--app-bg-subtle)",
};

const usageProgressFillStyle = (percent) => ({
    width: `${Math.max(0, Math.min(100, percent))}%`,
    height: "100%",
    backgroundColor: "primary.main",
    transition: "width 0.18s ease",
});

const usageProgressLabelStyle = {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--app-text-primary)",
    whiteSpace: "nowrap",
    lineHeight: 1,
    borderRadius: "8px",
    border: "1px solid var(--app-border-subtle)",
    backgroundColor: "var(--app-bg-surface)",
    padding: "5px 8px",
};

const WorkspaceSettingsModal = ({
    open,
    onClose,
    defaultTab = TAB_PROFILE,
    authContext,
    checkoutStatus,
    isCheckoutProcessing = false,
}) => {
    const fetchContext = useContext(FetchContext);
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [userState, setUserState] = useState({
        name: "",
        email: "",
        plan: null,
        subscriptionStatus: null,
        trialEndDate: null,
        nextBillingDate: null,
        transcriptionUsedSec: 0,
        enhancementUsedSec: 0,
        transcriptionLimitSec: 0,
        enhancementLimitSec: 0,
        usageCycleEnd: null,
        cancelAtPeriodEnd: false,
        cancelAt: null,
        stripeHasPaymentMethod: false,
        paymentMethodBrand: "",
        stripePaymentLastFour: "",
    });
    const [nameDraft, setNameDraft] = useState("");
    const [isSavingName, setIsSavingName] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isRequestingDelete, setIsRequestingDelete] = useState(false);
    const [deleteAccountError, setDeleteAccountError] = useState("");
    const [removePaymentConfirmOpen, setRemovePaymentConfirmOpen] = useState(false);
    const [isRemovingPayment, setIsRemovingPayment] = useState(false);
    const [removePaymentError, setRemovePaymentError] = useState("");
    const [paymentRemovedNotice, setPaymentRemovedNotice] = useState(false);
    const [isCheckoutStarting, setIsCheckoutStarting] = useState(false);
    const [cancelSubscriptionConfirmOpen, setCancelSubscriptionConfirmOpen] = useState(false);
    const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
    const [cancelSubscriptionError, setCancelSubscriptionError] = useState("");
    const [isResumingSubscription, setIsResumingSubscription] = useState(false);
    const [subscriptionActionNotice, setSubscriptionActionNotice] = useState("");

    useEffect(() => {
        if (!open) return;
        if (defaultTab === TAB_PLAN || defaultTab === TAB_PROFILE || defaultTab === TAB_USAGE) {
            setActiveTab(defaultTab);
        } else {
            setActiveTab(TAB_PROFILE);
        }
    }, [open, defaultTab]);

    useEffect(() => {
        if (open) return;
        setDeleteConfirmOpen(false);
        setIsRequestingDelete(false);
        setDeleteAccountError("");
        setRemovePaymentConfirmOpen(false);
        setIsRemovingPayment(false);
        setRemovePaymentError("");
        setPaymentRemovedNotice(false);
        setCancelSubscriptionConfirmOpen(false);
        setIsCancellingSubscription(false);
        setCancelSubscriptionError("");
        setIsResumingSubscription(false);
        setSubscriptionActionNotice("");
    }, [open]);

    const displayName = userState.name || authContext.getUserDisplayName?.() || "";
    const displayEmail = userState.email || authContext.getUserEmail?.() || "";

    const userId = authContext.getUserId?.();

    useEffect(() => {
        if (!open || !userId) return;
        const userRef = doc(db, kLibrettoUserCollectionName, userId);
        const unsubscribe = onSnapshot(
            userRef,
            (docSnap) => {
                const docData = docSnap.data();
                if (!docData) return;
                setUserState((prev) => {
                    const next = {
                        name: docData.displayName || "",
                        email: docData.email || "",
                        plan: docData.plan,
                        subscriptionStatus: docData.subscriptionStatus ?? null,
                        trialEndDate: docData.trialEndDate ?? null,
                        nextBillingDate: docData.nextBillingDate ?? null,
                        transcriptionUsedSec: docData.transcriptionUsedSec ?? 0,
                        enhancementUsedSec: docData.enhancementUsedSec ?? 0,
                        transcriptionLimitSec: docData.transcriptionLimitSec ?? 0,
                        enhancementLimitSec: docData.enhancementLimitSec ?? 0,
                        usageCycleEnd: docData.usageCycleEnd ?? null,
                        cancelAtPeriodEnd: docData.cancelAtPeriodEnd ?? false,
                        cancelAt: docData.cancelAt ?? null,
                        stripeHasPaymentMethod: docData.stripeHasPaymentMethod ?? false,
                        paymentMethodBrand: docData.paymentMethodBrand ?? "",
                        stripePaymentLastFour: docData.stripePaymentLastFour ?? "",
                    };
                    if (
                        prev.name === next.name &&
                        prev.email === next.email &&
                        prev.plan === next.plan &&
                        prev.subscriptionStatus === next.subscriptionStatus &&
                        prev.trialEndDate === next.trialEndDate &&
                        prev.nextBillingDate === next.nextBillingDate &&
                        prev.transcriptionUsedSec === next.transcriptionUsedSec &&
                        prev.enhancementUsedSec === next.enhancementUsedSec &&
                        prev.transcriptionLimitSec === next.transcriptionLimitSec &&
                        prev.enhancementLimitSec === next.enhancementLimitSec &&
                        prev.usageCycleEnd === next.usageCycleEnd &&
                        prev.cancelAtPeriodEnd === next.cancelAtPeriodEnd &&
                        prev.cancelAt === next.cancelAt &&
                        prev.stripeHasPaymentMethod === next.stripeHasPaymentMethod &&
                        prev.paymentMethodBrand === next.paymentMethodBrand &&
                        prev.stripePaymentLastFour === next.stripePaymentLastFour
                    ) {
                        return prev;
                    }
                    return next;
                });
            },
            (error) => {
                console.error("[WorkspaceSettingsModal] user snapshot error", error);
            }
        );
        return () => unsubscribe();
    }, [open, userId]);

    useEffect(() => {
        if (!open) return;
        setNameDraft(displayName);
    }, [open, displayName]);

    const isLifetimePlan =
        userState.plan !== null && String(userState.plan) === String(LibrettoPlan.StudioLifetime);
    const isTrialing = userState.subscriptionStatus === "trialing";
    const isActivePaid = userState.subscriptionStatus === "active";
    const isCancelScheduled = userState.cancelAtPeriodEnd === true;
    const hasPaymentMethod = userState.stripeHasPaymentMethod === true;
    const trialEndMillis = normalizeTimestampToMillis(userState.trialEndDate);
    const trialEnded = trialEndMillis != null && trialEndMillis < Date.now() && !isActivePaid && !isTrialing;
    const trialEndDateText = userState.trialEndDate ? formatUnixTimeBrief(userState.trialEndDate) : "";
    const nextBillingDateText = userState.nextBillingDate ? formatUnixTimeBrief(userState.nextBillingDate) : "";
    const cancelAtText = userState.cancelAt ? formatUnixTimeBrief(userState.cancelAt) : "";
    const effectiveCancelAtText = cancelAtText || nextBillingDateText || "";
    const effectiveCancelAtMillis = normalizeTimestampToMillis(userState.cancelAt ?? userState.nextBillingDate ?? null);
    const hasAccessUntilFutureDate = effectiveCancelAtMillis != null && effectiveCancelAtMillis > Date.now();
    const hasActiveSubscriptionAccess = isActivePaid || isTrialing || (isCancelScheduled && hasAccessUntilFutureDate);
    const canCancelSubscription = isActivePaid && !isCancelScheduled;
    const canResumeSubscription = isCancelScheduled;
    const canRemovePaymentMethod = hasPaymentMethod && !hasActiveSubscriptionAccess;
    const usageCycleEndUnix = userState.usageCycleEnd ?? userState.nextBillingDate ?? null;
    const usageCycleEndText = usageCycleEndUnix ? formatUnixTimeBrief(usageCycleEndUnix) : "";
    const usageCycleEndMillis = normalizeTimestampToMillis(usageCycleEndUnix);
    const billingCycleLabel =
        String(userState.plan) === String(LibrettoPlan.StudioAnnual) || String(userState.plan) === "annual"
            ? "Annual"
            : "Monthly";
    const transcriptionLimitSec = Number(userState.transcriptionLimitSec) > 0
        ? Number(userState.transcriptionLimitSec)
        : 30 * 60 * 60;
    const enhancementLimitSec = Number(userState.enhancementLimitSec) > 0
        ? Number(userState.enhancementLimitSec)
        : 10 * 60 * 60;
    const transcriptionUsedSec = Math.max(0, Number(userState.transcriptionUsedSec) || 0);
    const enhancementUsedSec = Math.max(0, Number(userState.enhancementUsedSec) || 0);
    const normalizedBrand = (userState.paymentMethodBrand || "").toLowerCase();
    const formattedBrand = normalizedBrand
        ? normalizedBrand === "amex" || normalizedBrand === "american express"
            ? "American Express"
            : normalizedBrand.charAt(0).toUpperCase() + normalizedBrand.slice(1)
        : "";
    const paymentMethodText =
        formattedBrand && userState.stripePaymentLastFour
            ? `${formattedBrand} ending in ${userState.stripePaymentLastFour}`
            : hasPaymentMethod
                ? "Payment method on file"
                : "No payment method";
    const usageResetCountdownText = (() => {
        if (usageCycleEndMillis == null) return "";
        const diffMs = usageCycleEndMillis - Date.now();
        if (diffMs <= 0) return "";
        const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
        return days === 1 ? "Resets in 1 day" : `Resets in ${days} days`;
    })();
    const usageHeaderText = usageResetCountdownText
        || (usageCycleEndText ? `Resets ${usageCycleEndText}` : (isLifetimePlan ? "Resets monthly" : "Cycle date pending"));
    const usageSummaryText = usageCycleEndText
        ? `Cycle resets ${usageCycleEndText}`
        : (isLifetimePlan ? "Cycle resets monthly" : "Cycle date pending");
    const currentPlanName = isLifetimePlan ? "Studio Lifetime" : "Studio";
    const studioPriceAfterTrialLabel = "$24/month";

    const formatUsageDuration = (sec) => {
        const safeSec = Math.max(0, Number(sec) || 0);
        const hours = safeSec / 3600;
        if (hours >= 10) return `${Math.round(hours)}h`;
        if (hours >= 1) return `${hours.toFixed(1)}h`;
        return `${Math.round(safeSec / 60)}m`;
    };

    const formatUsagePair = (usedSec, limitSec) => `${formatUsageDuration(usedSec)} / ${formatUsageDuration(limitSec)}`;

    const computeUsagePercent = (usedSec, limitSec) => {
        if (!limitSec || limitSec <= 0) return 0;
        const raw = (Math.max(0, usedSec) / limitSec) * 100;
        if (raw <= 0) return 0;
        return Math.max(6, Math.min(100, raw));
    };

    const handleNameSave = async () => {
        const trimmed = nameDraft.trim();
        if (!trimmed || trimmed === userState.name) {
            setNameDraft(userState.name || "");
            return;
        }
        if (!userId) return;
        setIsSavingName(true);
        try {
            const entityRef = doc(db, kLibrettoUserCollectionName, userId);
            await updateDoc(entityRef, {displayName: trimmed});
            sessionStorage.setItem("librettoDisplayName", trimmed);
            setUserState((prev) => ({...prev, name: trimmed}));
        } catch (error) {
            console.error("Error updating name:", error);
        } finally {
            setIsSavingName(false);
        }
    };

    const handleDeleteAccountClick = () => {
        setDeleteAccountError("");
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeleteAccount = async () => {
        if (isRequestingDelete) return;
        setIsRequestingDelete(true);
        setDeleteAccountError("");
        try {
            const token = await authContext.getFreshToken?.();
            if (!token) {
                setDeleteAccountError("Please sign in again to delete your account.");
                return;
            }
            await fetchContext.authAxios.post(
                "/account/delete",
                {},
                {headers: {Authorization: `Bearer ${token}`}}
            );
            await authContext.logout?.();
            setDeleteConfirmOpen(false);
            onClose?.();
        } catch (error) {
            console.error("Error requesting account deletion:", error);
            setDeleteAccountError("Failed to request account deletion. Please try again.");
        } finally {
            setIsRequestingDelete(false);
        }
    };

    const paymentBrandSvgs = {
        visa:
            '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="40" viewBox="0 0 64 40">' +
            '<rect width="64" height="40" rx="6" fill="#1A1F71"/>' +
            '<text x="32" y="25" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="16" font-weight="700" fill="#FFFFFF">VISA</text>' +
            '</svg>',
        mastercard:
            '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="40" viewBox="0 0 64 40">' +
            '<rect width="64" height="40" rx="6" fill="#FFFFFF"/>' +
            '<circle cx="26" cy="20" r="10" fill="#EB001B"/>' +
            '<circle cx="38" cy="20" r="10" fill="#F79E1B"/>' +
            '<text x="32" y="34" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="7" font-weight="600" fill="#111111">mastercard</text>' +
            '</svg>',
        amex:
            '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="40" viewBox="0 0 64 40">' +
            '<rect width="64" height="40" rx="6" fill="#2E77BC"/>' +
            '<text x="32" y="25" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="12" font-weight="700" fill="#FFFFFF">AMEX</text>' +
            '</svg>',
    };

    const getPaymentBrandIconSrc = () => {
        const brand = (userState.paymentMethodBrand || "").toLowerCase();
        const key = brand === "american express" ? "amex" : brand;
        const svg = paymentBrandSvgs[key];
        return svg ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` : null;
    };

    const renderPaymentBrandIcon = () => {
        const src = getPaymentBrandIconSrc();
        if (!src) {
            return <Box sx={paymentMethodIconStyle}>Card</Box>;
        }
        return <Box component="img" src={src} alt="" sx={paymentMethodImageStyle} />;
    };

    const handleRemovePaymentMethod = async () => {
        if (isRemovingPayment) return;
        setIsRemovingPayment(true);
        setRemovePaymentError("");
        try {
            await fetchContext.authAxios.delete('/payment', {
                headers: {
                    Authorization: `Bearer ${authContext.getToken()}`,
                },
            });
            setUserState((prev) => ({
                ...prev,
                stripeHasPaymentMethod: false,
                paymentMethodBrand: "",
                stripePaymentLastFour: "",
            }));
            setPaymentRemovedNotice(true);
            setRemovePaymentConfirmOpen(false);
        } catch (error) {
            console.error("Error removing payment method:", error);
            setRemovePaymentError("Failed to remove payment method. Please try again.");
        } finally {
            setIsRemovingPayment(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (isCancellingSubscription) return;
        setIsCancellingSubscription(true);
        setCancelSubscriptionError("");
        try {
            await fetchContext.authAxios.post(
                "/subscription/cancel",
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authContext.getToken()}`,
                    },
                }
            );
            setUserState((prev) => ({
                ...prev,
                cancelAtPeriodEnd: true,
            }));
            setSubscriptionActionNotice(
                effectiveCancelAtText
                    ? `Your subscription will remain active until ${effectiveCancelAtText}.`
                    : "Your subscription cancellation is scheduled for the end of the current billing period."
            );
            setCancelSubscriptionConfirmOpen(false);
        } catch (error) {
            console.error("Error canceling subscription:", error);
            setCancelSubscriptionError("Failed to cancel subscription. Please try again.");
        } finally {
            setIsCancellingSubscription(false);
        }
    };

    const handleResumeSubscription = async () => {
        if (isResumingSubscription) return;
        setIsResumingSubscription(true);
        setSubscriptionActionNotice("");
        try {
            await fetchContext.authAxios.post(
                "/subscription/resume",
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authContext.getToken()}`,
                    },
                }
            );
            setUserState((prev) => ({
                ...prev,
                cancelAtPeriodEnd: false,
                cancelAt: null,
            }));
            setSubscriptionActionNotice("Subscription renewed. Your plan will continue monthly.");
        } catch (error) {
            const status = error?.response?.status;
            if (status === 402) {
                setSubscriptionActionNotice("Add a payment method to resume your subscription.");
            } else {
                setSubscriptionActionNotice("Failed to resume subscription. Please try again.");
            }
            console.error("Error resuming subscription:", error);
        } finally {
            setIsResumingSubscription(false);
        }
    };

    const renderPlanDetail = (label, value) => (
        <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
            <Typography sx={labelTextStyle}>{label}</Typography>
            <Typography sx={valueTextStyle}>{value || "—"}</Typography>
        </Box>
    );

    const renderAfterTrialRow = () => (
        <Box sx={planInlineRowStyle}>
            <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
                <Typography sx={labelTextStyle}>After trial</Typography>
                <Typography sx={valueTextStyle}>{`${studioPriceAfterTrialLabel} \u2022 cancel anytime`}</Typography>
            </Box>
            <Button
                variant="contained"
                disableElevation
                onClick={handleAddPaymentMethodClick}
                disabled={isCheckoutStarting}
                sx={planPrimaryButtonStyle}
            >
                Add payment method to continue
            </Button>
        </Box>
    );

    const renderPaymentMethodRow = (showRemoveLink) => (
        <Box sx={planInlineRowStyle}>
            <Box sx={{display: "flex", flexDirection: "column", gap: "2px"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                    {renderPaymentBrandIcon()}
                    <Typography sx={valueTextStyle}>{paymentMethodText}</Typography>
                </Box>
                {showRemoveLink && (
                    <Button
                        variant="text"
                        onClick={() => {
                            setRemovePaymentError("");
                            setRemovePaymentConfirmOpen(true);
                        }}
                        sx={removePaymentLinkStyle}
                    >
                        Remove payment method
                    </Button>
                )}
            </Box>
            <Button
                variant="outlined"
                onClick={handleAddPaymentMethodClick}
                disabled={isCheckoutStarting}
                sx={planSecondaryButtonStyle}
            >
                {isCheckoutStarting ? "Opening..." : "Update payment method"}
            </Button>
        </Box>
    );

    const handleAddPaymentMethodClick = async () => {
        if (isCheckoutStarting) return;
        setIsCheckoutStarting(true);
        try {
            const currentUrl = new URL(window.location.href);
            const successUrl = new URL(currentUrl.href);
            successUrl.searchParams.set("checkout", "success");
            successUrl.searchParams.set("settings", "plan");
            const cancelUrl = new URL(currentUrl.href);
            cancelUrl.searchParams.set("checkout", "canceled");
            cancelUrl.searchParams.set("settings", "plan");
            const successPath = `${successUrl.pathname}${successUrl.search}${successUrl.search ? "&" : "?"}session_id={CHECKOUT_SESSION_ID}`;
            const response = await fetchContext.authAxios.get('/checkout', {
                headers: {
                    Authorization: `Bearer ${authContext.getToken()}`,
                },
                params: {
                    successPath,
                    cancelPath: `${cancelUrl.pathname}${cancelUrl.search}`,
                },
            });
            const checkoutUrl = response.data?.url || response.data?.Url;
            if (checkoutUrl) {
                window.location.assign(checkoutUrl);
                return;
            }
            const sessionId = response.data?.sessionId;
            const stripe = await StripePromise;
            if (!stripe || !sessionId) {
                setIsCheckoutStarting(false);
                return;
            }
            const result = await stripe.redirectToCheckout({sessionId});
            if (result.error) {
                console.error(result.error);
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
            setIsCheckoutStarting(false);
        }
    };

    const renderProfile = () => (
        <Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
            <Box sx={sectionTitleStyle}>Personal information</Box>
            <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                <Box sx={{display: "flex", flexDirection: "column", gap: "6px"}}>
                    <Typography sx={labelTextStyle}>Name</Typography>
                    <TextField
                        value={nameDraft}
                        onChange={(event) => setNameDraft(event.target.value)}
                        onBlur={handleNameSave}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                handleNameSave();
                            } else if (event.key === "Escape") {
                                setNameDraft(displayName);
                            }
                        }}
                        size="small"
                        disabled={isSavingName}
                        placeholder="Your name"
                        InputProps={{
                            style: {
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                lineHeight: "20px",
                                color: "var(--app-text-primary)",
                                height: "40px",
                                backgroundColor: "var(--app-bg-surface)",
                            },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    border: "1px solid var(--app-border-subtle)",
                                    borderRadius: "10px",
                                },
                                "&:hover fieldset": {
                                    border: "1px solid var(--app-border-muted)",
                                },
                                "&.Mui-focused fieldset": {
                                    border: "1px solid #2B6BFD",
                                },
                            },
                        }}
                    />
                </Box>
                <Box sx={{display: "flex", flexDirection: "column", gap: "6px"}}>
                    <Typography sx={labelTextStyle}>Email</Typography>
                    <Typography sx={valueTextStyle}>{displayEmail || "—"}</Typography>
                </Box>
            </Box>

            <Box sx={{marginTop: "auto"}}>
                <Box sx={{marginTop: "20px", ...sectionTitleStyle}}>Delete account</Box>
                <Box sx={{display: "flex", alignItems: "flex-start", gap: 16, justifyContent: "space-between"}}>
                    <Typography sx={{...mutedTextStyle, flex: 1}}>
                        Once you delete your account, your data, files, projects, and compositions will be gone forever.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleDeleteAccountClick}
                        sx={{
                            borderRadius: "999px",
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#C63636",
                            borderColor: "rgba(198, 54, 54, 0.4)",
                            "&:hover": {
                                borderColor: "#C63636",
                                backgroundColor: "rgba(255, 0, 0, 0.05)",
                            },
                        }}
                    >
                        Delete account
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    const renderPlan = () => {
        const showTrialNoCard = isTrialing && !hasPaymentMethod;
        const showTrialWithCard = isTrialing && hasPaymentMethod;
        const showTrialEnded = trialEnded && !isActivePaid;
        const showPaidPlanState = !showTrialNoCard && !showTrialWithCard && !showTrialEnded;

        const headerText = showTrialEnded
            ? "Trial ended"
            : showTrialNoCard || showTrialWithCard
                ? "Studio - Free trial"
                : isLifetimePlan
                    ? "Lifetime"
                    : (isCancelScheduled && effectiveCancelAtText
                        ? `Studio - cancels ${effectiveCancelAtText}`
                        : `Studio - ${studioPriceAfterTrialLabel}`);

        return (
            <>
                <Typography sx={sectionTitleStyle}>Plan</Typography>
                {isLifetimePlan ? (
                    <Box sx={planCardStyle}>
                        <Typography sx={{...valueTextStyle, fontSize: "15px", fontWeight: 600}}>
                            Lifetime
                        </Typography>
                        <Typography sx={mutedTextStyle}>
                            Thanks for supporting Libretto early. Your account includes all core features with no ongoing
                            subscription.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{display: "flex", flexDirection: "column", gap: "12px", height: "100%"}}>
                        <Box sx={planCardStyle}>
                            <Typography sx={{...valueTextStyle, fontSize: "15px", fontWeight: 600}}>
                                {headerText}
                            </Typography>
                            {isCheckoutProcessing && checkoutStatus === "success" && !hasPaymentMethod && (
                                <Typography sx={paymentNoticeStyle}>Updating payment method...</Typography>
                            )}
                            {showTrialEnded ? (
                                <Box sx={{display: "flex", flexDirection: "column", gap: "10px"}}>
                                    <Typography sx={mutedTextStyle}>
                                        Your free trial ended on {trialEndDateText || "—"}.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        onClick={handleAddPaymentMethodClick}
                                        disabled={isCheckoutStarting}
                                        sx={planPrimaryButtonStyle}
                                    >
                                        Add payment method
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                                    {showTrialNoCard && renderPlanDetail("Status", "Active")}
                                    {(showTrialNoCard || showTrialWithCard) &&
                                        renderPlanDetail("Trial ends", trialEndDateText)}
                                    {showTrialWithCard &&
                                        renderPlanDetail("Price after trial", studioPriceAfterTrialLabel)}
                                    {showTrialNoCard && renderAfterTrialRow()}
                                    {showTrialWithCard && renderPlanDetail("Billing starts", trialEndDateText)}
                                    {showTrialWithCard && renderPlanDetail("Billing cycle", billingCycleLabel)}
                                    {showTrialWithCard && renderPaymentMethodRow(false)}
                                    {showPaidPlanState && (
                                        <>
                                            {renderPlanDetail(
                                                "Billing cycle",
                                                isCancelScheduled && effectiveCancelAtText
                                                    ? `${billingCycleLabel}, cancels ${effectiveCancelAtText}`
                                                    : nextBillingDateText
                                                        ? `${billingCycleLabel}, renews ${nextBillingDateText}`
                                                        : billingCycleLabel
                                            )}
                                            {isCancelScheduled && renderPlanDetail(
                                                "Status",
                                                effectiveCancelAtText ? `Active until ${effectiveCancelAtText}` : "Cancellation scheduled"
                                            )}
                                            {renderPaymentMethodRow(canRemovePaymentMethod)}
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {showPaidPlanState && (
                            <Box sx={{marginTop: "auto"}}>
                                <Box sx={{display: "flex", alignItems: "flex-start", gap: 16, justifyContent: "space-between"}}>
                                    <Typography sx={{...mutedTextStyle, flex: 1}}>
                                        {canResumeSubscription
                                            ? (effectiveCancelAtText
                                                ? `Your subscription is set to cancel on ${effectiveCancelAtText}. Resume to keep Studio active.`
                                                : "Your subscription is set to cancel at period end. Resume to keep Studio active.")
                                            : "Your subscription stays active until the end of the current billing period if you cancel."}
                                    </Typography>
                                    {canResumeSubscription ? (
                                        <Button
                                            variant="outlined"
                                            onClick={handleResumeSubscription}
                                            disabled={isResumingSubscription}
                                            sx={{
                                                borderRadius: "999px",
                                                textTransform: "none",
                                                fontFamily: "Inter",
                                                fontWeight: 600,
                                                color: "var(--app-text-primary)",
                                                borderColor: "var(--app-border-subtle)",
                                                "&:hover": {
                                                    borderColor: "var(--app-border-muted)",
                                                    backgroundColor: "var(--app-bg-subtle)",
                                                },
                                            }}
                                        >
                                            {isResumingSubscription ? "Resuming..." : "Resume subscription"}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setCancelSubscriptionError("");
                                                setSubscriptionActionNotice("");
                                                setCancelSubscriptionConfirmOpen(true);
                                            }}
                                            disabled={!canCancelSubscription}
                                            sx={{
                                                borderRadius: "999px",
                                                textTransform: "none",
                                                fontFamily: "Inter",
                                                fontWeight: 600,
                                                color: "#C63636",
                                                borderColor: "rgba(198, 54, 54, 0.4)",
                                                "&:hover": {
                                                    borderColor: "#C63636",
                                                    backgroundColor: "rgba(255, 0, 0, 0.05)",
                                                },
                                            }}
                                        >
                                            Cancel subscription
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                        {subscriptionActionNotice ? (
                            <Typography sx={paymentNoticeStyle}>{subscriptionActionNotice}</Typography>
                        ) : null}
                        {paymentRemovedNotice ? (
                            <Typography sx={paymentNoticeStyle}>
                                Payment method removed successfully.
                            </Typography>
                        ) : null}
                    </Box>
                )}
            </>
        );
    };

    const renderUsageRow = ({label, usedSec, limitSec}) => {
        const percent = computeUsagePercent(usedSec, limitSec);
        return (
            <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                <Typography sx={labelTextStyle}>{label}</Typography>
                <Box sx={usageProgressTrackStyle}>
                    <Box sx={usageProgressFillStyle(percent)} />
                    <Typography sx={usageProgressLabelStyle}>{formatUsagePair(usedSec, limitSec)}</Typography>
                </Box>
            </Box>
        );
    };

    const renderUsage = () => (
        <>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px"}}>
                <Box sx={{display: "flex", alignItems: "baseline", gap: "10px"}}>
                    <Typography sx={{...sectionTitleStyle, fontSize: "15px"}}>Current plan</Typography>
                    <Typography sx={{...valueTextStyle, fontSize: "15px", fontWeight: 600}}>
                        {currentPlanName}
                    </Typography>
                </Box>
                <Typography sx={{...mutedTextStyle, fontSize: "15px"}}>
                    {usageHeaderText}
                </Typography>
            </Box>

            <Box sx={usageSummaryCardStyle}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px"}}>
                    <Typography sx={sectionTitleStyle}>Summary</Typography>
                    <Typography sx={mutedTextStyle}>
                        {usageSummaryText}
                    </Typography>
                </Box>
                {renderUsageRow({
                    label: "Monthly transcription used",
                    usedSec: transcriptionUsedSec,
                    limitSec: transcriptionLimitSec,
                })}
                {renderUsageRow({
                    label: "Monthly audio enhancement used",
                    usedSec: enhancementUsedSec,
                    limitSec: enhancementLimitSec,
                })}
            </Box>
        </>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: "18px",
                    width: "920px",
                    maxWidth: "calc(100vw - 40px)",
                    height: "70vh",
                    maxHeight: "650px",
                    backgroundColor: "var(--app-bg-canvas)",
                },
            }}
        >
            <DialogContent sx={{padding: 0, height: "100%", position: "relative"}}>
                {/*<IconButton*/}
                {/*    onClick={onClose}*/}
                {/*    sx={{*/}
                {/*        position: "absolute",*/}
                {/*        top: -1,*/}
                {/*        right: -1,*/}
                {/*        color: "#6B7280",*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <CloseIcon fontSize="small" />*/}
                {/*</IconButton>*/}
                <Box sx={{display: "flex", height: "100%"}}>
                    <Box
                        sx={{
                            width: "260px",
                            padding: "24px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        <Typography sx={sidebarHeaderStyle}>Account</Typography>
                        <Box sx={sidebarItemStyle(activeTab === TAB_PROFILE)} onClick={() => setActiveTab(TAB_PROFILE)}>
                            Profile
                        </Box>
                        <Box sx={sidebarItemStyle(activeTab === TAB_PLAN)} onClick={() => setActiveTab(TAB_PLAN)}>
                            Plan
                        </Box>
                        <Box sx={sidebarItemStyle(activeTab === TAB_USAGE)} onClick={() => setActiveTab(TAB_USAGE)}>
                            Usage
                        </Box>
                    </Box>
                    <Box sx={{flex: 1, padding: "20px 20px 20px 0"}}>
                        <Box
                            sx={{
                                height: "100%",
                                backgroundColor: "var(--app-bg-surface)",
                                borderRadius: "14px",
                                border: "1px solid var(--app-border-subtle)",
                                padding: "24px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "20px",
                                overflowY: "auto",
                            }}
                        >
                            {activeTab === TAB_PROFILE && renderProfile()}
                            {activeTab === TAB_PLAN && renderPlan()}
                            {activeTab === TAB_USAGE && renderUsage()}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <Dialog
                open={open && deleteConfirmOpen}
                onClose={() => {
                    if (isRequestingDelete) return;
                    setDeleteConfirmOpen(false);
                }}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                        border: "1px solid var(--app-border-subtle)",
                        padding: "2px",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        paddingBottom: "6px",
                    }}
                >
                    Delete account?
                </DialogTitle>
                <DialogContent sx={{paddingTop: 0}}>
                    <Typography sx={mutedTextStyle}>
                        This will request deletion of your Libretto account and sign you out. You won’t be able to log
                        in again.
                    </Typography>
                    {deleteAccountError ? (
                        <Typography sx={{...mutedTextStyle, marginTop: "10px", color: "#C63636"}}>
                            {deleteAccountError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{padding: "16px 24px 20px 24px", gap: "10px"}}>
                    <Button
                        variant="outlined"
                        disabled={isRequestingDelete}
                        onClick={() => setDeleteConfirmOpen(false)}
                        sx={{
                            borderRadius: "999px",
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "var(--app-text-primary)",
                            borderColor: "var(--app-border-subtle)",
                            "&:hover": {
                                borderColor: "var(--app-border-muted)",
                                backgroundColor: "var(--app-bg-subtle)",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={isRequestingDelete}
                        onClick={handleConfirmDeleteAccount}
                        sx={{
                            borderRadius: "999px",
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            backgroundColor: "#C63636",
                            "&:hover": {
                                backgroundColor: "#B52F2F",
                            },
                        }}
                    >
                        {isRequestingDelete ? "Deleting…" : "Delete account"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={open && cancelSubscriptionConfirmOpen}
                onClose={() => {
                    if (isCancellingSubscription) return;
                    setCancelSubscriptionConfirmOpen(false);
                }}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                        border: "1px solid var(--app-border-subtle)",
                        padding: "2px",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        paddingBottom: "6px",
                    }}
                >
                    Cancel Studio subscription?
                </DialogTitle>
                <DialogContent sx={{paddingTop: 0}}>
                    <Typography sx={mutedTextStyle}>
                        Your Studio plan will remain active until {effectiveCancelAtText || "the end of the current billing period"}.
                        After that, your account will return to the free plan and you won’t be charged again.
                    </Typography>
                    <Box sx={{marginTop: "12px"}}>
                        <Typography sx={{...labelTextStyle, color: "var(--app-text-primary)"}}>
                            Before you cancel
                        </Typography>
                        <Typography sx={{...mutedTextStyle, marginTop: "6px"}}>
                            You’ll lose access to exporting new episodes and paid cleanup and editing features.
                        </Typography>
                        <Typography sx={{...mutedTextStyle}}>
                            Ongoing Studio access ends after {effectiveCancelAtText || "your current billing period"}.
                        </Typography>
                    </Box>
                    {cancelSubscriptionError ? (
                        <Typography sx={{...mutedTextStyle, marginTop: "10px", color: "#C63636"}}>
                            {cancelSubscriptionError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{padding: "16px 24px 20px 24px", gap: "10px", display: "flex", justifyContent: "space-between"}}>
                    <Button
                        variant="text"
                        component="a"
                        href="mailto:contact@libretto.fm"
                        sx={{
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "var(--app-text-muted)",
                            "&:hover": {
                                backgroundColor: "transparent",
                                textDecoration: "underline",
                            },
                        }}
                    >
                        Contact us
                    </Button>
                    <Box sx={{display: "flex", gap: "10px"}}>
                        <Button
                            variant="outlined"
                            disabled={isCancellingSubscription}
                            onClick={() => setCancelSubscriptionConfirmOpen(false)}
                            sx={{
                                borderRadius: "10px",
                                height: "35px",
                                minHeight: "35px",
                                padding: "0 16px",
                                textTransform: "none",
                                fontFamily: "Inter",
                                fontWeight: 600,
                                color: "var(--app-text-primary)",
                                borderColor: "var(--app-border-subtle)",
                                "&:hover": {
                                    borderColor: "var(--app-border-muted)",
                                    backgroundColor: "var(--app-bg-subtle)",
                                },
                            }}
                        >
                            Keep Studio
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={isCancellingSubscription}
                            onClick={handleCancelSubscription}
                            sx={{
                                borderRadius: "10px",
                                height: "35px",
                                minHeight: "35px",
                                padding: "0 16px",
                                textTransform: "none",
                                fontFamily: "Inter",
                                fontWeight: 600,
                                backgroundColor: "#C63636",
                                "&:hover": {
                                    backgroundColor: "#B52F2F",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "var(--app-border-muted)",
                                    color: "var(--app-text-subtle)",
                                },
                            }}
                        >
                            {isCancellingSubscription ? "Canceling..." : "Cancel subscription"}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog
                open={open && removePaymentConfirmOpen}
                onClose={() => {
                    if (isRemovingPayment) return;
                    setRemovePaymentConfirmOpen(false);
                }}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                        border: "1px solid var(--app-border-subtle)",
                        padding: "2px",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        paddingBottom: "6px",
                    }}
                >
                    Remove payment method?
                </DialogTitle>
                <DialogContent sx={{paddingTop: 0}}>
                    <Typography sx={mutedTextStyle}>
                        This removes your saved payment method from your account.
                    </Typography>
                    <Typography sx={{...mutedTextStyle, marginTop: "8px"}}>
                        You can add a payment method again anytime from this screen.
                    </Typography>
                    {removePaymentError ? (
                        <Typography sx={{...mutedTextStyle, marginTop: "10px", color: "#C63636"}}>
                            {removePaymentError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{padding: "16px 24px 20px 24px", gap: "10px"}}>
                    <Button
                        variant="outlined"
                        disabled={isRemovingPayment}
                        onClick={() => setRemovePaymentConfirmOpen(false)}
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "var(--app-text-primary)",
                            borderColor: "var(--app-border-subtle)",
                            "&:hover": {
                                borderColor: "var(--app-border-muted)",
                                backgroundColor: "var(--app-bg-subtle)",
                            },
                        }}
                    >
                        Keep payment method
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={isRemovingPayment}
                        onClick={handleRemovePaymentMethod}
                        sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            backgroundColor: "#C63636",
                            "&:hover": {
                                backgroundColor: "#B52F2F",
                            },
                        }}
                    >
                        {isRemovingPayment ? "Removing..." : "Remove payment method"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default WorkspaceSettingsModal;
